import type { RosterEntry } from './incidentRoster/incidentRoster.types';

export type ResponsibleIndividualOption = { label: string; value: string };

/**
 * Users currently sitting in the selected ICS position. Exited members are
 * skipped so a closed assignment cannot be named as responsible.
 */
export function responsibleIndividualOptions(
  roster: RosterEntry[] | undefined,
  positionId: string | null | undefined
): ResponsibleIndividualOption[] {
  if (!positionId) return [];
  return (roster ?? [])
    .filter((member) => !member.exited)
    .filter((member) =>
      (member.incidentPositions ?? []).some(
        (position) => position.positionId === positionId
      )
    )
    .filter((member) => member.userId && (member.userName || member.emailAddress))
    .map((member) => ({
      value: member.userId,
      label: member.userName ?? (member.emailAddress as string),
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

/** Drops ids that are no longer occupants of the newly selected seat. */
export function pruneResponsibleIndividualIds(
  ids: string[],
  occupantOptions: ResponsibleIndividualOption[]
): string[] {
  const allowed = new Set(occupantOptions.map((option) => option.value));
  return ids.filter((id) => allowed.has(id));
}

/**
 * Occupant picks plus labels for stored ids that left the seat, so an edit
 * does not turn a named person into a raw guid chip.
 */
export function responsibleIndividualDisplayOptions(
  occupantOptions: ResponsibleIndividualOption[],
  selectedIds: string[],
  rosterUsers: ResponsibleIndividualOption[]
): ResponsibleIndividualOption[] {
  const byId = new Map(occupantOptions.map((option) => [option.value, option]));
  selectedIds.forEach((id) => {
    if (byId.has(id)) return;
    const fromRoster = rosterUsers.find((option) => option.value === id);
    byId.set(id, fromRoster ?? { value: id, label: id });
  });
  return [...byId.values()];
}
